import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageHero, SectionHeading, FeatureCard, BreadcrumbBar } from "@/components/site/Blocks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Crumb } from "@/lib/schema";

type LinkTarget = { to: string; label: string };

/** Shared Dark-Navy keyword landing page (EN/TR). Content is passed in; no invented facts. */
export function SeoLandingPage(props: {
  trail: readonly Crumb[];
  eyebrow: string;
  title: string;
  intro: string;
  lead: string;
  stats: Array<{ value: string; label: string }>;
  featuresEyebrow: string;
  featuresTitle: string;
  featuresLead: string;
  features: Array<{ title: string; body: string }>;
  faqTitle: string;
  faqs: Array<[string, string]>;
  ctaTitle: string;
  ctaBody: string;
  primary: LinkTarget;
  secondary: LinkTarget[];
}) {
  return (
    <>
      <PageHero eyebrow={props.eyebrow} title={props.title} intro={props.intro} />
      <BreadcrumbBar trail={props.trail} />

      <section className="section-shell py-16">
        <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">{props.lead}</p>
        <dl className="mt-10 grid grid-cols-2 gap-6 md:grid-cols-4">
          {props.stats.map((s) => (
            <div key={s.label} className="border-t border-border pt-4">
              <dt className="text-sm text-muted-foreground">{s.label}</dt>
              <dd className="mt-1 text-3xl font-extrabold tabular-nums text-primary">{s.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="bg-secondary">
        <div className="section-shell py-20">
          <SectionHeading eyebrow={props.featuresEyebrow} title={props.featuresTitle} lead={props.featuresLead} />
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {props.features.map((item, i) => (
              <FeatureCard key={item.title} title={item.title} index={String(i + 1).padStart(2, "0")}>
                {item.body}
              </FeatureCard>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell py-20">
        <SectionHeading eyebrow="FAQ" title={props.faqTitle} />
        <Accordion type="single" collapsible className="mt-8 max-w-3xl">
          {props.faqs.map(([q, a], i) => (
            <AccordionItem key={q} value={`faq-${i}`}>
              <AccordionTrigger className="text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-ink text-ink-foreground">
        <div className="section-shell py-16">
          <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight">{props.ctaTitle}</h2>
          <p className="mt-4 max-w-2xl text-ink-muted">{props.ctaBody}</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
            <Link
              to={props.primary.to}
              className="inline-flex h-[52px] items-center justify-center gap-2 rounded-xl bg-background px-6 font-extrabold text-primary"
            >
              {props.primary.label} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
            {props.secondary.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex h-[52px] items-center justify-center rounded-xl border-[1.5px] border-ink-foreground/35 px-6 font-extrabold"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
